/**
 * Reusable webhook endpoints — the library the agent form's two selects choose
 * from. THIS SCREEN IS THE LIST AND THE CREATE FORM ONLY; a row click opens
 * /webhooks/:id (WebhookEdit.tsx), which owns editing, testing and the signing
 * secret. Same split as Tools/ToolEdit.
 *
 * A ROW HERE IS AN ADDRESS PLUS A SECRET, NOT A SUBSCRIPTION. Nothing fires
 * because a row exists; an agent points its init-webhook or end-of-session slot
 * at one, and that agent's sessions do the firing. The same endpoint can serve
 * both slots on many agents, which is why this is a library page and not a
 * field on the agent.
 *
 * The secrets story lives with the fields that carry them: signingSecret (ours,
 * shown) and header values (the customer's, write-only) are both on the
 * endpoint's own page — see WebhookEdit.tsx's header. Creation navigates
 * straight there, because pasting the freshly-minted secret into the receiver
 * is the next step and that is where the secret is shown.
 */
import { useEffect, useState } from 'react'
import { webhooks, type CreateWebhookBody, type WebhookView } from '@/api'
import { can } from '@/lib/auth'
import { hostOf } from '@/lib/tool-form'
import { webhookMessageFor as messageFor } from '@/lib/webhook-form'
import { useAuth } from '@/components/auth-provider'
import { navigate } from '@/components/app-sidebar'
import { DataTable, PageHeader, type Column } from '@/components/data-table'
import { buildHeaders, headerRowsClean, HeadersEditor, type HeaderRow } from '@/components/headers-editor'
import { fmtWhen } from '@/components/state-pill'
import { WebhookGuide } from '@/components/webhook-guide'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function WebhooksPage() {
  const { user } = useAuth()
  const canWrite = can(user, 'write')

  const [rows, setRows] = useState<WebhookView[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  /** The create form starts CLOSED — the table is this page's face. */
  const [showCreate, setShowCreate] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    webhooks
      .list(ac.signal)
      .then((res) => {
        if (ac.signal.aborted) return
        setRows(res.rows)
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(messageFor(e))
      })
    return () => ac.abort()
  }, [])

  // ------------------------------------------------------------------ create
  const [name, setName] = useState('')
  const [url, setUrl] = useState('')
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([])
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (creating) return
    if (!headerRowsClean(headerRows, true)) {
      setCreateError('Fix the header rows first — each needs a valid name and a value.')
      return
    }
    const body: CreateWebhookBody = { name: name.trim(), url: url.trim() }
    if (headerRows.length > 0) body.headers = buildHeaders(headerRows)
    setCreating(true)
    setCreateError(null)
    webhooks
      .create(body, AbortSignal.timeout(20_000))
      .then((row) => {
        // Straight to the endpoint's own page: the freshly-minted signing
        // secret is there, and pasting it into the receiver is the next step.
        navigate(`/webhooks/${row.id}`)
      })
      .catch((err: unknown) => {
        setCreating(false)
        setCreateError(messageFor(err))
      })
  }

  // ------------------------------------------------------------------- table
  const columns: Column<WebhookView>[] = [
    {
      key: 'name',
      header: 'Name',
      width: '18rem',
      cell: (w) => (
        <span className="flex items-center gap-1.5">
          <span className="truncate font-medium" title={w.name}>
            {w.name}
          </span>
          {!w.enabled && (
            <span
              className="pill pill-plain shrink-0"
              title="Inactive: agents keep the reference, but neither the init nor the end-of-session webhook fires. Flip it on the endpoint's page."
            >
              inactive
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'url',
      header: 'Endpoint',
      cell: (w) => (
        // Host only in the table; the full URL is on the endpoint's page. A
        // webhook URL's path can carry a token.
        <span className="mono block truncate text-xs" title="Host only here — open the endpoint for the full URL.">
          {hostOf(w.url)}
        </span>
      ),
    },
    {
      key: 'headers',
      header: 'Headers',
      width: '6rem',
      hideBelow: 'md',
      cell: (w) => (
        <span
          className="text-sm"
          title={
            w.headerNames.length === 0
              ? 'No custom headers.'
              : `Names only — values are write-only: ${w.headerNames.join(', ')}`
          }
        >
          {w.headerNames.length}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      width: '11rem',
      hideBelow: 'lg',
      cell: (w) => <span className="whitespace-nowrap">{fmtWhen(w.createdAt)}</span>,
    },
  ]

  return (
    <div className="min-w-0">
      <PageHeader
        title="Webhooks"
        subtitle={
          <>
            Endpoints of your own that sessions call: an agent&rsquo;s <em>init webhook</em>{' '}
            fires before the model connects and may rewrite the prompt, the greeting and the
            client data payload; its <em>end-of-session webhook</em> receives the transcript
            and stats after the recording finalises. Define the endpoint here once, then
            point any agent at it from the agent&rsquo;s own page.
          </>
        }
        actions={
          <>
            {/* Docs, so every role gets it — reading the contract is not a write. */}
            <WebhookGuide />
            {canWrite && (
              <Button size="sm" className="h-8" onClick={() => setShowCreate((v) => !v)}>
                {showCreate ? 'Close the form' : 'New endpoint'}
              </Button>
            )}
          </>
        }
      />

      {canWrite && showCreate ? (
        <section className="panel">
          <h2>
            Add an endpoint
            <span className="note">
              the signing secret is minted on create and shown on the endpoint&rsquo;s page
            </span>
          </h2>
          <form onSubmit={onCreate} className="body flex flex-col gap-4">
            <div className="grid2">
              <div className="field">
                <Label htmlFor="whName">Name</Label>
                <Input
                  id="whName"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ServiceNow — identity lookup"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  For humans choosing it on the agent form. Renameable, unlike a tool.
                </p>
              </div>
              <div className="field">
                <Label htmlFor="whUrl">Endpoint URL</Label>
                <Input
                  id="whUrl"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="mono"
                  placeholder="https://hooks.example.com/voice"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
                <p className="text-xs text-muted-foreground">
                  HTTPS, reachable from the public internet. Private addresses and credentials
                  in the URL are refused at save time and re-checked at fire time.
                </p>
              </div>
            </div>

            <HeadersEditor idPrefix="whCreate" rows={headerRows} onChange={setHeaderRows} />

            <div className="flex flex-wrap items-center gap-3">
              <Button type="submit" disabled={creating || name.trim() === '' || url.trim() === ''}>
                {creating ? 'Creating…' : 'Create endpoint'}
              </Button>
              {createError && <p className="err text-sm">{createError}</p>}
            </div>
          </form>
        </section>
      ) : null}
      {!canWrite && (
        <p className="mb-4 text-sm text-muted-foreground">
          Read-only: adding and editing endpoints is a write, and this account is a{' '}
          <span className="mono">{user?.role}</span>. Each row&rsquo;s details, signing secret
          included, are open to you — click one.
        </p>
      )}

      {loadError && <p className="err mb-4">{loadError}</p>}

      <DataTable
        columns={columns}
        rows={rows}
        keyOf={(w) => w.id}
        // Fixed layout so no cell can force horizontal scroll; a row click opens
        // the endpoint's own page, which holds the full URL, the secret, the
        // editor and the test panel.
        fixed
        onRowClick={(w) => navigate(`/webhooks/${w.id}`)}
        emptyTitle="No webhook endpoints"
        emptyBody={
          <>
            Nothing is defined yet. Agents work fine without webhooks — this table only
            matters when a session should call out to your own systems at its start or end.
          </>
        }
      />
    </div>
  )
}
